import { z } from 'zod';

import { LeadFormField, LeadFormFieldName } from '@/types/lead-magnet';

type LeadFieldInputType = 'text' | 'email' | 'select';

interface LeadFieldDefinition {
	type: LeadFieldInputType;
	schema: z.ZodType<string>;
	allowedPattern?: RegExp;
	crmField: 'name' | 'company' | 'email' | 'jobTitle';
}

const NAME_PATTERN = /^[A-Za-zА-Яа-яЁёІіЇїЄєҐґ\s'-]*$/;

export const LEAD_FORM_FIELD_REGISTRY: Record<LeadFormFieldName, LeadFieldDefinition> = {
	fullName: {
		type: 'text',
		schema: z.string().trim().min(2, 'Name is required'),
		allowedPattern: NAME_PATTERN,
		crmField: 'name'
	},
	companyName: {
		type: 'text',
		schema: z.string().trim().min(2, 'Company name is required'),
		crmField: 'company'
	},
	email: {
		type: 'email',
		schema: z.email('Invalid email'),
		crmField: 'email'
	},
	role: {
		type: 'select',
		schema: z.string().trim().min(1, 'Select your role'),
		crmField: 'jobTitle'
	}
};

export const LEAD_FORM_FIELD_NAMES = Object.keys(LEAD_FORM_FIELD_REGISTRY) as LeadFormFieldName[];

export const isLeadFormFieldName = (value: unknown): value is LeadFormFieldName =>
	typeof value === 'string' && value in LEAD_FORM_FIELD_REGISTRY;

export const getLeadFieldDefinition = (name: LeadFormFieldName) => LEAD_FORM_FIELD_REGISTRY[name];

export const buildLeadSchema = (fields: LeadFormField[]) => {
	const shape = fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
		const definition = LEAD_FORM_FIELD_REGISTRY[field.name];

		if (!definition) return acc;

		acc[field.name] = field.required ? definition.schema : definition.schema.or(z.literal(''));

		return acc;
	}, {});

	return z.object(shape);
};

export type LeadFormData = Partial<Record<LeadFormFieldName, string>>;

export const buildLeadFormDefaults = (fields: LeadFormField[]): LeadFormData =>
	fields.reduce<LeadFormData>((acc, field) => ({ ...acc, [field.name]: '' }), {});
